import { Box, IconButton, Popover, Stack, ToggleButton, ToggleButtonGroup, getSpacing } from "@compass/core";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Skeleton, Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  UserSettingPageSizeOption,
  setUserSettingPageSize,
  setUserSettingSortMethod,
  userSettingPageSizeOptions,
} from "redux/reducers/userSettingSlice";
import { RootState } from "redux/store";
import ApiService from "services/apiService";
import SettingsIcon from "@mui/icons-material/Settings";
import { ReactNode, useState } from "react";

export const ListFooter = (props: {
  total: number | undefined;
  page?: number;
  size?: number;
  onPageChange?: (value: number) => void;
  onSizeChange?: (value: number) => void;
  onSortMethodChange?: (value: boolean) => void;
}) => {
  const dispatch = useDispatch();
  const [settingAnchorEl, setSettingAnchorEl] = useState<HTMLButtonElement | null>(null);
  const userPageSize = useSelector((state: RootState) => state.userSetting.pageSize);
  const userMultiSort = useSelector((state: RootState) => state.userSetting.multiSort);
  const settingLabelWidth = "200px";

  const pagination = () => {
    let pageInfoNode: ReactNode;
    let pagerNode: ReactNode;

    if (props.page !== undefined && props.size !== undefined) {
      const page = props.page;
      const size = props.size;
      const start = page * size - size + 1;
      const end = props.total !== undefined ? (page * size > props.total ? props.total : page * size) : page * size;

      pageInfoNode = (
        <>
          <Typography variant="inherit">
            {start}-{end}
          </Typography>
          <Typography variant="inherit">of</Typography>
        </>
      );

      pagerNode = (
        <Stack direction="row">
          <IconButton onClick={() => props.onPageChange?.(page - 1)} disabled={page === 1}>
            <KeyboardArrowLeftIcon />
          </IconButton>
          <IconButton onClick={() => props.onPageChange?.(page + 1)} disabled={!props.total || end === props.total}>
            <KeyboardArrowRightIcon />
          </IconButton>
        </Stack>
      );
    }

    let totalNode: ReactNode =
      props.total !== undefined ? (
        <Typography variant="inherit">
          {props.total}
          {!pageInfoNode ? " total" : ""}
        </Typography>
      ) : (
        <Skeleton width="50px" />
      );

    return (
      <Stack direction="row" alignItems={"center"} spacing="md">
        <Stack direction="row" spacing="xs" alignItems={"center"}>
          {pageInfoNode}

          {totalNode}
        </Stack>

        {pagerNode}
      </Stack>
    );
  };

  function setting() {
    if (!props.onSizeChange && !props.onSortMethodChange) return null;

    return (
      <Box>
        <IconButton onClick={(e) => setSettingAnchorEl(e.currentTarget)}>
          <SettingsIcon fontSize="small" />
        </IconButton>

        <Popover
          open={Boolean(settingAnchorEl)}
          anchorEl={settingAnchorEl}
          onClose={() => setSettingAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <Stack p={getSpacing("md")} spacing="md">
            {props.onSizeChange && pageSizeOption}
            {props.onSortMethodChange && sortMethodOption}
          </Stack>
        </Popover>
      </Box>
    );
  }

  const pageSizeOption = (
    <Stack direction="row" spacing="md" alignItems="center">
      <Typography variant="body1" width={settingLabelWidth}>
        Items per page:
      </Typography>

      <ToggleButtonGroup
        fullWidth
        value={userPageSize}
        exclusive
        onChange={(event: React.MouseEvent<HTMLElement>, value: UserSettingPageSizeOption) => {
          if (!value) return;
          props.onSizeChange?.(value);
          dispatch(setUserSettingPageSize(value));
          ApiService.userSetting.savePageSize(value);
        }}
      >
        {userSettingPageSizeOptions.map((option) => (
          <ToggleButton key={option} value={option}>
            {option}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );

  const sortMethodOption = (
    <Stack direction="row" spacing="md" alignItems="center">
      <Typography variant="body1" width={settingLabelWidth}>
        Sort method:
      </Typography>

      <ToggleButtonGroup
        fullWidth
        value={userMultiSort}
        exclusive
        onChange={(event: React.MouseEvent<HTMLElement>, value: boolean) => {
          if (value === undefined) return;
          props.onSortMethodChange?.(value);
          dispatch(setUserSettingSortMethod(value));
          ApiService.userSetting.saveSortMethod(value);
        }}
      >
        <ToggleButton value={false}>Single-Sort</ToggleButton>
        <ToggleButton value={true}>Multi-Sort</ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );

  return (
    <Stack direction="row" spacing="md" justifyContent={"space-between"} alignItems={"center"} height="35px">
      <Box>{setting()}</Box>

      <Box>{pagination()}</Box>
    </Stack>
  );
};
